import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  BarChart3Icon,
  ChevronRightIcon,
  FileTextIcon,
  PencilIcon,
} from "lucide-react";
import type { FormListItemDTO } from "@/modules/forms/presentation/api/types";
import { FormStatusBadge } from "@/modules/forms/presentation/ui/FormStatusBadge";
import { formatDateTime } from "@/shared/lib/date-time";

type FormsRecentFormsCardProps = {
  forms: FormListItemDTO[];
  isLoading: boolean;
  onViewAll: () => void;
  onEdit: (id: string) => void;
  onResponses: (id: string) => void;
};

export function FormsRecentFormsCard({
  forms,
  isLoading,
  onViewAll,
  onEdit,
  onResponses,
}: FormsRecentFormsCardProps) {
  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2 space-y-0">
        <div className="space-y-1">
          <CardTitle className="text-lg">ຟອມອັບເດດຫຼ້າສຸດ</CardTitle>
          <CardDescription>ຈັດລຳດັບຕາມເວລາປັບປຸງຫຼ້າສຸດ</CardDescription>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-1"
          onClick={onViewAll}
        >
          ເບິ່ງທັງໝົດ
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading && forms.length === 0 ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        ) : forms.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-muted/20 px-6 py-10 text-center">
            <FileTextIcon className="h-10 w-10 text-muted-foreground/60" />
            <div className="space-y-1">
              <p className="font-medium text-sm">ຍັງບໍ່ມີຟອມ</p>
              <p className="text-muted-foreground text-xs">
                ເລີ່ມສ້າງຟອມໃໝ່ເພື່ອເປີດຮັບຄຳຕອບ
              </p>
            </div>
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {forms.map((f) => (
              <li
                key={f.id}
                className="flex flex-col gap-3 rounded-xl border bg-card p-4 transition-colors hover:bg-muted/30 sm:flex-row sm:items-center"
              >
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to="/app/forms/$id/edit"
                      params={{ id: f.id }}
                      className="truncate font-medium text-primary hover:underline"
                    >
                      {f.title}
                    </Link>
                    <FormStatusBadge status={f.status} />
                  </div>
                  <p className="text-muted-foreground text-xs">
                    ປ່ຽນຫຼ້າສຸດ {formatDateTime(f.updatedAt)}
                    {f.categoryName ? ` · ${f.categoryName}` : ""}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {f.responseCount > 0 ? (
                    <Badge variant="outline" className="tabular-nums">
                      {f.responseCount} ຄຳຕອບ
                    </Badge>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(f.id)}
                  >
                    <PencilIcon className="mr-1.5 h-3.5 w-3.5" />
                    ແກ້ໄຂ
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onResponses(f.id)}
                  >
                    <BarChart3Icon className="mr-1.5 h-3.5 w-3.5" />
                    ຜົນຕອບ
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
